import { useMemo, useEffect, useState } from "react";
import { Download } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../../../components/ui/accordion";
import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../../components/ui/dropdown-menu";
import { LoadingSpinner } from "../../../../components/ui/loadingSpinner/loadingSpinner";
import {
  WorkPaperTemplate,
  WorkPaperTemplateWithFieldsResponse,
  TemplateFieldResponse,
  FieldState,
  getFieldTypeLabel,
  FieldType,
  GetTemplateResponseSchema,
  FieldTypeEnum,
  ComputedTableValue,
} from "../../../../models/workPaperTemplate";
import { ControlSessionEnvelope } from "../../../../models/audit/controlSession";
import type { SessionKind } from "../../../../hooks/audit/useControlSessionEnvelope";
import { ControlAttribute } from "../../../../models/audit/control";
import {
  AuditSession,
  sourceRowSchema,
} from "../../../../models/audit/auditSession";
import { useHookCache } from "../../../../lib/hookCache";
import { useControl } from "../../../../hooks/audit/useControl";
import { FieldValueCard } from "./FieldValueCard";
import { SupplementaryDocumentsDropdown } from "./SupplementaryDocumentsDropdown";
import { SupplementaryDocumentsModal } from "./SupplementaryDocumentsModal";
import { SendToOptroButton } from "../../../../components/audit/SendToOptroButton";
import { fetchFastApi } from "../../../../api/fastapi";
import { z } from "zod";

type SourceRow = z.infer<typeof sourceRowSchema>;

interface GeneratedFieldsDisplayProps {
  template: WorkPaperTemplate;
  fieldStates: FieldState[];
  loading: boolean;
  onGenerate: () => void;
  generating: boolean;
  onUpdateFieldValue?: (fieldId: string, newValue: string) => Promise<void>;
  updatingFieldId?: string | null;
  onDownload?: () => void;
  onDownloadBundle?: () => void;
  downloading?: boolean;
  controlSession: ControlSessionEnvelope | null;
  /** Which entity `controlSession` hangs off — the supplementary-document
   * routes below are served by a different router per kind. */
  sessionKind: SessionKind;
  onUpdateAttributeDescription?: (
    attributeId: string,
    description: string,
  ) => Promise<void>;
  updatingAttributeId?: string | null;
  onUpdateComputedTableValue?: (
    fieldId: string,
    tableValue: ComputedTableValue,
  ) => Promise<void>;
  updatingComputedTableFieldId?: string | null;
}

export const TemplateExportDisplay = ({
  template,
  fieldStates,
  loading,
  onGenerate,
  generating,
  onUpdateFieldValue,
  updatingFieldId,
  onDownload,
  onDownloadBundle,
  downloading,
  controlSession,
  sessionKind,
  onUpdateAttributeDescription,
  updatingAttributeId,
  onUpdateComputedTableValue,
  updatingComputedTableFieldId,
}: GeneratedFieldsDisplayProps) => {
  const [templateWithFields, setTemplateWithFields] =
    useState<WorkPaperTemplateWithFieldsResponse | null>(null);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [documentRefreshTrigger, setDocumentRefreshTrigger] = useState(0);

  // Load template with fields to get field metadata
  useEffect(() => {
    const loadTemplate = async () => {
      setTemplateLoading(true);
      try {
        const response = await fetchFastApi({
          path: `/work-paper-templates/${template.id}`,
          method: "GET",
        });
        const parsed = GetTemplateResponseSchema.parse(response);
        setTemplateWithFields(parsed.template);
      } catch (error) {
        console.error("Error loading template:", error);
        setTemplateWithFields(null);
      } finally {
        setTemplateLoading(false);
      }
    };

    loadTemplate();
  }, [template.id]);

  // Create a map of field states by fieldId
  const fieldStatesByFieldId = useMemo(() => {
    const map = new Map<string, FieldState>();
    for (const state of fieldStates) {
      map.set(state.fieldId, state);
    }
    return map;
  }, [fieldStates]);

  // Combine template fields with their state
  const fieldsWithState = useMemo(() => {
    if (!templateWithFields) return [];

    return templateWithFields.fields.map((field) => ({
      field,
      state: fieldStatesByFieldId.get(field.id),
    }));
  }, [templateWithFields, fieldStatesByFieldId]);

  // Group fields by type
  const fieldsByType = useMemo(() => {
    const groups: Record<
      FieldType,
      Array<{ field: TemplateFieldResponse; state?: FieldState }>
    > = {
      static: [],
      dynamic: [],
      attribute_summary_table: [],
      sample_summary_table: [],
      detail_testing_table: [],
      citation_asset_sheet: [],
      computed_table: [],
    };

    for (const item of fieldsWithState) {
      groups[item.field.fieldType].push(item);
    }

    return groups;
  }, [fieldsWithState]);

  // The Phase-1 envelope's `control` field is `DryControl` and does NOT
  // carry `attributes`. Fetch the hydrated `ControlWithProcedure` via the
  // bridge hook so the Excel export can resolve attribute references. A
  // work-step session has no `controlId`; its attributes ride the envelope.
  const { data: hydratedControl } = useControl(controlSession?.controlId);
  const attributes =
    controlSession?.workStep?.attributes ?? hydratedControl?.attributes;

  // Create lookup maps for attributes and samples
  const attributeLookup = useMemo(() => {
    const lookup = new Map<string, ControlAttribute>();
    if (attributes) {
      for (const attribute of attributes) {
        lookup.set(attribute.id, attribute);
      }
    }
    return lookup;
  }, [attributes]);

  const { getEntry } = useHookCache();
  const sampleLookup = useMemo(() => {
    const lookup = new Map<string, SourceRow>();
    if (!controlSession?.procedureSessions) return lookup;
    // Phase 2: source rows for a procedure session are owned by the per-
    // procedure cache slice. We read whatever has been hydrated.
    for (const ps of controlSession.procedureSessions) {
      const entry = getEntry<AuditSession>(["procedure-session", ps.id]);
      const sourceRows = entry?.data?.sourceRows ?? [];
      for (const sourceRow of sourceRows) {
        lookup.set(sourceRow.id, sourceRow);
      }
    }
    return lookup;
  }, [controlSession, getEntry]);

  const hasAnyFields = fieldsWithState.length > 0;
  const hasAnyValues = fieldStates.some(
    (state) => state.computedValue !== null,
  );

  if (templateLoading || loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <LoadingSpinner />
      </div>
    );
  }

  if (!templateWithFields) {
    return (
      <div className="text-sm text-muted-foreground">
        Failed to load template details
      </div>
    );
  }

  if (!hasAnyFields) {
    return (
      <div className="border rounded-lg p-8 text-center">
        <p className="text-muted-foreground">
          This template has no fields defined. Edit the template to add fields.
        </p>
      </div>
    );
  }

  // TODO: Replace field type grouping with proper section support from backend
  // TODO: Add section names and ordering when backend adds section_id to fields

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Generated Field Values</h2>
        <div className="flex items-center gap-2">
          {controlSession && (
            <SupplementaryDocumentsDropdown
              sessionId={controlSession.id}
              sessionKind={sessionKind}
              onUploadComplete={() => {
                setDocumentRefreshTrigger((prev) => prev + 1);
              }}
              onViewDocuments={() => setShowDocumentsModal(true)}
            />
          )}
          <Button
            onClick={onGenerate}
            loading={generating}
            disabled={generating}
          >
            {hasAnyValues ? "Regenerate All Values" : "Generate Values"}
          </Button>
          {onDownload &&
            (controlSession?.auditorTrailEnabled === true &&
            onDownloadBundle ? (
              // Auditor-trail workspaces can also download the evidence bundle (the combined
              // workpaper plus annotated source documents, zipped), so the Download action becomes a
              // split button.
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    loading={downloading}
                    disabled={downloading}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Download Workpaper
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={onDownload}>
                    Workpaper (.xlsx)
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={onDownloadBundle}
                    className="flex items-center gap-2"
                  >
                    Evidence Bundle (.zip)
                    <Badge
                      variant="secondary"
                      className="text-[10px] font-medium uppercase tracking-wide"
                    >
                      Beta
                    </Badge>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                onClick={onDownload}
                loading={downloading}
                disabled={downloading}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Download Workpaper
              </Button>
            ))}
          {controlSession && (
            <SendToOptroButton
              controlSession={controlSession}
              sessionKind={sessionKind}
              selectedTemplateId={template.id}
              generating={generating}
            />
          )}
        </div>
      </div>

      <Accordion
        type="multiple"
        defaultValue={Object.keys(fieldsByType)}
        className="w-full"
      >
        {(
          Object.entries(fieldsByType) as [
            FieldType,
            Array<{ field: TemplateFieldResponse; state?: FieldState }>,
          ][]
        ).map(([fieldType, items]) => {
          if (items.length === 0) return null;

          const sectionLabel = getFieldTypeLabel(fieldType);
          const itemsWithValues = items.filter((item) => {
            const state = item.state;
            return (
              state &&
              (state.staticValue !== null ||
                state.computedValue !== null ||
                state.attributeTableValue !== null ||
                state.sampleTableValue !== null ||
                state.detailTestingTableValue !== null ||
                state.citationAssetSheetValue !== null)
            );
          });

          return (
            <AccordionItem key={fieldType} value={fieldType}>
              <AccordionTrigger>
                <div className="flex items-center gap-2">
                  <span>{sectionLabel}</span>
                  {fieldType === FieldTypeEnum.enum.dynamic && (
                    <span className="text-sm text-muted-foreground">
                      ({itemsWithValues.length}/{items.length} generated)
                    </span>
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4 pt-2">
                  {items.map((item) => (
                    <FieldValueCard
                      key={item.field.id}
                      field={item.field}
                      fieldState={item.state}
                      onUpdate={onUpdateFieldValue}
                      updating={updatingFieldId === item.field.id}
                      attributeLookup={attributeLookup}
                      sampleLookup={sampleLookup}
                      onUpdateAttributeDescription={
                        onUpdateAttributeDescription
                      }
                      updatingAttributeId={updatingAttributeId}
                      onUpdateComputedTableValue={onUpdateComputedTableValue}
                      updatingComputedTableFieldId={
                        updatingComputedTableFieldId
                      }
                    />
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      {controlSession && (
        <SupplementaryDocumentsModal
          open={showDocumentsModal}
          onOpenChange={setShowDocumentsModal}
          sessionId={controlSession.id}
          sessionKind={sessionKind}
          key={documentRefreshTrigger}
          onDocumentDeleted={() => {
            setDocumentRefreshTrigger((prev) => prev + 1);
          }}
        />
      )}
    </div>
  );
};
